/**
 * Full-screen activation panel — Sai speaks.
 *
 * Anti-misclick (JB 2026-05-24):
 *   - The backdrop NO LONGER dismisses on tap. A player tapping the
 *     Spark rapidly used to accidentally close the activation before
 *     reading. Now only the explicit "продолжить" button closes.
 *   - The button is a <DelayedButton> that fills over 1 second; click
 *     during the fill does nothing. By the time it's clickable, the
 *     player has had enough time to read.
 *
 * Respects prefers-reduced-motion via Framer Motion's reduced-motion
 * handling and the DelayedButton component itself.
 */
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { useGameStore } from '../stores/game.js';
import { emit } from '../telemetry/emit.js';
import { DelayedButton } from './DelayedButton.js';
import styles from './ActivationOverlay.module.css';

export function ActivationOverlay(): JSX.Element {
  const activation = useGameStore((s) => s.activeActivation);
  const epoch = useGameStore((s) => s.state.epoch);
  const dismiss = useGameStore((s) => s.dismissActivation);
  // UX analytics — отслеживаем shown/dismissed с длительностью чтения.
  const shownAtRef = useRef<number | null>(null);
  const shownIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (activation !== null && shownIdRef.current !== activation.id) {
      shownAtRef.current = Date.now();
      shownIdRef.current = activation.id;
      emit('activation.shown', { activation_id: activation.id, epoch });
    } else if (activation === null && shownIdRef.current !== null) {
      const id = shownIdRef.current;
      const duration = shownAtRef.current !== null ? Date.now() - shownAtRef.current : 0;
      emit('activation.dismissed', {
        activation_id: id,
        duration_ms: duration,
        // Сейчас всегда «button» — единственный путь dismiss'а через
        // DelayedButton. Когда добавим auto-timeout — менять на 'auto'.
        dismissed_by: 'button',
      });
      shownAtRef.current = null;
      shownIdRef.current = null;
    }
  }, [activation, epoch]);

  return (
    <AnimatePresence>
      {activation !== null && (
        <motion.div
          className={styles.overlay}
          role="dialog"
          aria-modal="true"
          aria-label="Активация Сай"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          /* 2026-05-26 polish: backdrop fade длиннее (700ms) — голос
             Сай приходит МЕДЛЕННО, не «всплывает» как обычный modal.
             Это про ритуал, не про utility. */
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <motion.article
            className={styles.panel}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            /* Panel чуть позже backdrop'а (delay 0.3s) — игрок успевает
               «осознать что что-то приходит» до появления текста. */
            transition={{ duration: 0.7, ease: [0.25, 0.8, 0.3, 1], delay: 0.3 }}
          >
            {activation.preface !== undefined && (
              <motion.p
                className={styles.preface}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                {activation.preface}
              </motion.p>
            )}
            {/* Главный текст приходит ПОСЛЕ preface'а — staggered cascade.
                Pause между preface и text создаёт ритм «вдох — слово». */}
            <motion.p
              className={styles.text}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: 0.8,
                delay: activation.preface !== undefined ? 1.1 : 0.6,
              }}
            >
              {activation.text}
            </motion.p>
            <DelayedButton onClick={dismiss} delayMs={1000} ariaLabel="Продолжить">
              продолжить
            </DelayedButton>
          </motion.article>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
